<template>
  <div ref="feedRoot" class="cm-feed" v-if="cmSectionState !== 'empty' || query || terms.length">
    <div class="cm-feed-head">
      <div class="cm-search">
        <input
          v-model="query"
          type="text"
          :placeholder="$t('CM_FEED_SEARCH')"
          @keydown.enter.prevent="applySearch"
        />
        <button class="btn-st-tr" @click="applySearch">{{ $t('CM_FEED_FIND') }}</button>
      </div>
    </div>

    <div v-if="terms.length" class="cm-chips" style="margin-bottom: 8px">
      <span v-for="(t, i) in terms" :key="t" class="cm-chip is-tracked">
        {{ t }}
        <span class="cm-chip-x" @click="removeTerm(i)">&times;</span>
      </span>
    </div>

    <div class="sorting-panel">
      <div class="text">{{ $t('CM_FEED_SORT') }}</div>
      <ul class="sorts">
        <li v-for="s in sorts" :key="s.key">
          <button
            :class="`btn-st-grey ${sortClass(s.key)}`"
            @click="setSort(s.key)"
          >
            {{ s.label }}
          </button>
        </li>
      </ul>
      <div class="switch-view">
        <span :class="{ active: feedView === 'list' }" @click="feedView = 'list'">
          <i class="bi bi-list"></i>
        </span>
        <span :class="{ active: feedView === 'grid' }" @click="feedView = 'grid'">
          <i class="bi bi-columns-gap"></i>
        </span>
      </div>
    </div>

    <paginate
      v-if="totalPages > 1"
      v-model="page"
      :page-count="totalPages"
      :click-handler="setPage"
      :prev-text="$t('FEED_PREV')"
      :next-text="$t('FEED_NEXT')"
      :container-class="'pagination'"
      :page-class="'page-item'"
    />

    <div v-if="loading && !items.length" class="cm-state">{{ $t('CM_LOADING') }}</div>
    <div v-else-if="error && !items.length" class="cm-state is-error">{{ error }}</div>

    <template v-else>
      <div class="one-page" :class="{ 'is-masonry': feedView === 'grid' }">
        <div :class="{ panel: feedView === 'list' }">
          <ul ref="postsList" class="posts" :class="{ 'is-masonry': feedView === 'grid' }">
            <!-- image-analysis у поста, а не у ленты: разбор картинок платный, и
                 платит владелец страницы, а рядом лежат свои посты и расшаренные -->
            <Cm-feed-one
              v-for="post in items"
              :key="post.id"
              :post="post"
              :page="pageOf(post)"
              :layout="feedView"
              :image-analysis="post.imageAnalysis !== false"
              @analyse="onAnalyse"
            />
          </ul>
        </div>
      </div>

      <p v-if="!items.length" class="no-posts">{{ $t('NO_POSTS') }}</p>

      <div class="cm-pager">
        <span>{{ total }} {{ $t('CM_POSTS_SHORT') }}</span>
        <div v-if="loadingMore" class="cm-state">{{ $t('CM_LOADING') }}</div>
        <div v-else-if="error" class="cm-state is-error">{{ error }}</div>
      </div>

      <Cm-image-prompt
        v-if="vision.open === 'prompt'"
        :loading="!!vision.loading"
        :image-url="visionImageUrl"
        @run="runWithPrompt"
        @close="closeVision"
      />

      <Cm-image-result
        v-if="vision.open === 'result'"
        :item="vision.result"
        :image-url="visionImageUrl"
        :loading="!!vision.loading"
        :error="vision.error"
        @rerun="rerun"
        @edit-prompt="editPrompt"
        @close="closeVision"
      />
    </template>
  </div>
</template>

<script>
import Paginate from "vuejs-paginate";
import Masonry from "masonry-layout";
import imagesLoaded from "imagesloaded";
import { throttle } from "lodash";
import cmScopeMixin from "@/mixins/cmScopeMixin";
import cmSectionMixin from "@/mixins/cmSectionMixin";
import cmApi, {
  customImagePrompt,
  fetchImageDescriptions,
} from "@/api/controllers/content-monitoring";
import bus from "@/models/content-monitoring/bus";
import CmFeedOne from "./Cm-feed-one";
import CmImagePrompt from "./Cm-image-prompt";
import CmImageResult from "./Cm-image-result";
import pageVis from "@/models/page-visibility";

const FEED_VIEW_KEY = "cmFeedView";

function readFeedView() {
  try {
    return localStorage.getItem(FEED_VIEW_KEY) === "list" ? "list" : "grid";
  } catch (e) {
    return "grid";
  }
}

function writeFeedView(view) {
  try {
    localStorage.setItem(FEED_VIEW_KEY, view === "grid" ? "grid" : "list");
  } catch (e) {}
}

export default {
  name: "Cm-feed",
  mixins: [cmScopeMixin, cmSectionMixin],
  components: { Paginate, CmFeedOne, CmImagePrompt, CmImageResult },

  data() {
    return {
      items: [],
      total: 0,
      // разбор картинки: пост и вложение, открытая модалка, ответ модели
      vision: { post: null, target: null, open: "", result: null, error: "", loading: "" },
      page: 1,
      perPage: 10,
      query: "",
      terms: [],
      // источник слов фильтра: 'media' — пришли с картинки (drill-down чипа), '' — вкладка
      termSource: "",
      sort: "activity",
      order: "desc",
      loading: false,
      loadingMore: false,
      error: "",
      lastScopeKey: "",
      view: readFeedView(),
    };
  },

  computed: {
    /** Картинка для модалок разбора: у ролика — обложка, у VK-плеера её нет. */
    visionImageUrl() {
      const { post, target } = this.vision;
      if (!post || !target) return "";
      const item = (post.images || []).find((m) => m.index === target.index);
      return (item && item.image_url) || "";
    },

    feedView: {
      get() {
        return this.view;
      },
      set(val) {
        this.view = val === "grid" ? "grid" : "list";
        writeFeedView(this.view);
      },
    },
    sorts() {
      const pages = this.pages || [];
      const has = (metric) => pageVis.visibilityByPages(metric, pages);
      const list = [
        { key: "activity", label: this.$t("CM_SORT_ACTIVITY") },
        { key: "discussion", label: this.$t("CM_SORT_DISCUSSION") },
        { key: "date", label: this.$t("CM_SORT_DATE") },
        { key: "er", label: "ER", visible: has("erPost") },
        { key: "tr", label: "TR", visible: has("tr") },
        { key: "likes", label: this.$t("SORT_LIKES"), visible: has("likes") },
        { key: "comments", label: this.$t("SORT_COMMS"), visible: has("comments") },
        { key: "shares", label: this.$t("SORT_SHARES"), visible: has("reposts") },
        { key: "views", label: this.$t("SORT_VIEWS"), visible: has("views") },
      ];
      return list.filter((s) => s.visible !== false);
    },

    hasData() {
      return this.total > 0;
    },

    totalPages() {
      return Math.ceil(this.total / this.perPage);
    },

    hasMore() {
      return this.page < this.totalPages;
    },

    infiniteDisabled() {
      return this.loading || this.loadingMore || !this.hasMore;
    },

    pageMap() {
      return (this.pages || []).reduce((map, p) => {
        map[String(p.page_id)] = p;
        return map;
      }, {});
    },
  },

  watch: {
    cmScopeKey() {
      this.closeVision();
    },

    sorts(list) {
      if (list.some((s) => s.key === this.sort)) return;
      this.sort = "activity";
      this.order = "desc";
      this.page = 1;
      this.cmReload();
    },
    feedView() {
      this.scheduleMasonry();
    },
    items(next, prev) {
      if (this.feedView !== "grid") return;
      const appended =
        this._masonry &&
        prev &&
        next.length > prev.length &&
        prev.every((p, i) => p.id === next[i].id);
      if (appended) this.scheduleMasonryAppend(prev.length);
      else this.scheduleMasonry();
    },
    loading(val) {
      if (!val) this.scheduleMasonry();
    },
  },

  methods: {
    /**
     * Клик по значку на плитке: серый ведёт в запрос к модели, зелёный — в
     * готовый разбор. Состояние и модалки держит лента, а не карточка: плитку
     * masonry двигает трансформом, а внутри трансформа ломается position: fixed
     * (и перенос модалки в body ломал патч Vue — вторая модалка не вставлялась,
     * ответ модели не показывался, первая не закрывалась).
     */
    onAnalyse({ post, target, analyzed }) {
      if (!post || !target || this.vision.loading) return;
      this.vision = {
        post, target, open: analyzed ? "result" : "prompt",
        result: null, error: "", loading: "",
      };
      if (analyzed) this.loadSavedAnalysis();
    },

    closeVision() {
      this.vision = { ...this.vision, open: "", result: null, error: "" };
    },

    /** Сохранённый разбор — читаем с сервера, модель не дёргаем. */
    loadSavedAnalysis() {
      const { post, target } = this.vision;
      this.vision.loading = target.url;
      fetchImageDescriptions(post.page_id, post.post_id)
        .then((items) => {
          const found = items.find((x) => Number(x.media_index) === Number(target.index));
          if (found) this.vision.result = found;
          else this.vision.error = this.$t("CM_IMG_RESULT_MISSING");
        })
        .catch((e) => {
          this.vision.error = e.message;
        })
        .finally(() => {
          this.vision.loading = "";
        });
    },

    /** Запуск из модалки запроса: правленый промпт уже сохранён, шлём его же. */
    runWithPrompt() {
      this.describeImage({ force: true });
    },

    /** Повтор из модалки результата: обычный или сразу дорогой моделью. */
    rerun(options) {
      this.describeImage({ force: true, ...options });
    },

    /** «Изменить промпт» из результата: та же картинка, но с правкой запроса. */
    editPrompt() {
      this.vision.error = "";
      this.vision.open = "prompt";
    },

    describeImage(options = {}) {
      const { post, target } = this.vision;
      if (!post || !target || this.vision.loading) return;

      this.vision.open = "result";   // модалка результата покажет и ход разбора
      this.vision.result = null;
      this.vision.error = "";
      this.vision.loading = target.url;

      cmApi
        .describeImage({
          pageId: post.page_id,
          postId: post.post_id,
          imageUrl: target.url,
          force: !!options.force,
          tier: options.tier || 0,
          prompt: customImagePrompt(),
        })
        .then((data) => {
          this.vision.result = data;
          // разбор своим промптом сервер не сохраняет — и значок не зеленеет
          const index = Number(target.index);
          if (!data.custom_prompt && post.analyzed && post.analyzed.indexOf(index) === -1) {
            post.analyzed.push(index);
          }
        })
        .catch((e) => {
          this.vision.result = null;
          this.vision.error = e.message;
        })
        .finally(() => {
          this.vision.loading = "";
        });
    },

    pageOf(post) {
      return this.pageMap[String(post.page_id)] || {};
    },

    scheduleMasonry() {
      this._masonryToken = (this._masonryToken || 0) + 1;
      const token = this._masonryToken;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== this._masonryToken) return;
            this.setupMasonry();
          });
        });
      });
    },

    scheduleMasonryAppend(fromIndex) {
      this._masonryToken = (this._masonryToken || 0) + 1;
      const token = this._masonryToken;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== this._masonryToken) return;
            this.appendMasonryItems(fromIndex);
          });
        });
      });
    },

    setupMasonry() {
      this.teardownMasonry();
      if (this.feedView !== "grid" || (this.loading && !this.items.length)) return;
      const el = this.$refs.postsList;
      if (!el || !this.items.length) return;

      this._masonry = new Masonry(el, {
        itemSelector: "li",
        percentPosition: true,
        gutter: 12,
        transitionDuration: 0,
        horizontalOrder: false,
      });

      this.bindMasonryImages(el);
      this.observeMasonryItems(el.querySelectorAll("li"));
    },

    appendMasonryItems(fromIndex) {
      if (!this._masonry || this.feedView !== "grid") {
        this.setupMasonry();
        return;
      }
      const el = this.$refs.postsList;
      if (!el) return;
      const lis = Array.prototype.filter.call(el.children, (node) => node.tagName === "LI");
      const added = lis.slice(fromIndex);
      if (!added.length) return;
      this._masonry.appended(added);
      this.observeMasonryItems(added);
      this.bindMasonryImages(el);
    },

    bindMasonryImages(el) {
      if (this._masonryImages) {
        this._masonryImages.off("progress", this.relayoutMasonry);
        this._masonryImages.off("always", this.relayoutMasonry);
      }
      this._masonryImages = imagesLoaded(el);
      this._masonryImages.on("progress", this.relayoutMasonry);
      this._masonryImages.on("always", this.relayoutMasonry);
    },

    observeMasonryItems(nodes) {
      if (typeof ResizeObserver === "undefined") return;
      if (!this._masonryRo) {
        this._masonryRo = new ResizeObserver(this.relayoutMasonry);
      }
      Array.prototype.forEach.call(nodes, (node) => {
        if (node && node.nodeType === 1) this._masonryRo.observe(node);
      });
    },

    relayoutMasonry() {
      clearTimeout(this._masonryTimer);
      this._masonryTimer = setTimeout(() => {
        if (this._masonry) this._masonry.layout();
      }, 40);
    },

    teardownMasonry() {
      clearTimeout(this._masonryTimer);
      this._masonryTimer = null;
      if (this._masonryImages) {
        this._masonryImages.off("progress", this.relayoutMasonry);
        this._masonryImages.off("always", this.relayoutMasonry);
        this._masonryImages = null;
      }
      if (this._masonryRo) {
        this._masonryRo.disconnect();
        this._masonryRo = null;
      }
      if (this._masonry) {
        this._masonry.destroy();
        this._masonry = null;
      }
    },

    sortClass(key) {
      if (this.sort !== key) return "";
      return `active ${this.order === "desc" ? "positive" : "negative"}`;
    },

    setSort(key) {
      if (this.sort === key) {
        this.order = this.order === "desc" ? "asc" : "desc";
      } else {
        this.sort = key;
        this.order = "desc";
      }
      this.page = 1;
      this.load();
    },

    setPage(num) {
      this.page = num;
      this.load().then((fresh) => {
        if (fresh) this.$nextTick(() => this.scrollFeedToTop());
      });
    },

    onWindowScroll() {
      if (this._inactive || this.infiniteDisabled) return;
      const el = this.$refs.feedRoot;
      if (!el) return;
      if (el.getBoundingClientRect().bottom <= window.innerHeight + 400) {
        this.loadMore();
      }
    },

    loadMore() {
      if (this._inactive || this.infiniteDisabled || this._loadMoreLock) return;
      this._loadMoreLock = true;
      this.loadingMore = true;
      this.error = "";
      const nextPage = this.page + 1;
      this.page = nextPage;
      this.load({ append: true, silent: true })
        .then((fresh) => {
          if (!fresh && this.page === nextPage) this.page -= 1;
        })
        .finally(() => {
          this._loadMoreLock = false;
          this.loadingMore = false;
          this.$nextTick(() => this.maybeLoadMore());
        });
    },

    maybeLoadMore() {
      if (this._inactive || this.infiniteDisabled) return;
      const leftover =
        document.documentElement.scrollHeight - window.innerHeight - window.pageYOffset;
      if (leftover < 400) this.loadMore();
    },

    applySearch() {
      this.page = 1;
      this.load();
    },

    filterByTerm(term, source) {
      this.terms = Array.isArray(term) ? [...term] : [term].filter(Boolean);
      this.termSource = source === "media" || source === "text" ? source : "";
      this.page = 1;
      this.$nextTick(() => {
        this.load({ silent: true }).finally(() => {
          this.$nextTick(() => {
            requestAnimationFrame(() => this.scrollFeedToTop());
          });
        });
      });
    },

    scrollFeedToTop() {
      const el = this.$refs.feedRoot || (this.$el && this.$el.nodeType === 1 ? this.$el : null);
      if (!el) return;

      const header = document.querySelector(".header");
      const offset = (header && header.offsetHeight) || 0;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const top = el.getBoundingClientRect().top + scrollY - offset - 8;

      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    },

    removeTerm(index) {
      this.terms = this.terms.filter((_, i) => i !== index);
      if (!this.terms.length) this.termSource = "";
      this.page = 1;
      this.load();
    },

    load(options = {}) {
      const scopeChanged = this.lastScopeKey !== this.cmScopeKey;
      if (scopeChanged) {
        this.lastScopeKey = this.cmScopeKey;
        this.page = 1;
      }

      const append = !!options.append && this.page > 1;
      const cacheSuffix = [
        this.page,
        this.query,
        (this.terms || []).join("|"),
        this.termSource,
        this.sort,
        this.order,
      ].join("::");

      return this.cmLoad(
        () =>
          cmApi.fetchFeed(
            {
              ...this.cmScope,
              page: this.page,
              perPage: this.perPage,
              query: this.query,
              term: this.terms,
              termSource: this.termSource,
              sort: this.sort,
              order: this.order,
            },
            this.scope,
          ),
        (data) => {
          const next = (data && data.items) || [];
          this.total = (data && data.total) || 0;
          if (append) {
            const seen = new Set(this.items.map((p) => p.id));
            const extra = next.filter((p) => !seen.has(p.id));
            this.items = this.items.concat(extra);
            if (!next.length) this.total = this.items.length;
          } else {
            this.items = next;
          }
        },
        { silent: options.silent || scopeChanged || append, cacheSuffix, noCache: append },
      );
    },
  },

  created() {
    this.view = readFeedView();
    this._onWindowScroll = throttle(this.onWindowScroll, 200);
    bus.$on("cm:filter-term", this.filterByTerm);
  },

  mounted() {
    window.addEventListener("scroll", this._onWindowScroll, { passive: true });
    this.scheduleMasonry();
  },

  // вкладка CM на главной живёт в keep-alive: скрытая лента не должна
  // слушать скролл чужой вкладки — её корень вне DOM и всегда «у низа»
  activated() {
    if (!this._inactive) return;
    this._inactive = false;
    window.addEventListener("scroll", this._onWindowScroll, { passive: true });
  },

  deactivated() {
    this._inactive = true;
    this._onWindowScroll.cancel();
    window.removeEventListener("scroll", this._onWindowScroll);
  },

  beforeDestroy() {
    if (this._onWindowScroll) {
      this._onWindowScroll.cancel();
      window.removeEventListener("scroll", this._onWindowScroll);
    }
    this.teardownMasonry();
    bus.$off("cm:filter-term", this.filterByTerm);
  },
};
</script>
